import { CHESS_MODEL_STRATEGY } from "./chessProbability";
import { boardScoresFromResult } from "./chessOlympiadResults";
import { buildBoardProbabilities, calculateTeamMatchProbabilities } from "./chessTeamMatch";
import {
  initialOlympiadRanking,
  olympiadStandings,
  pairOlympiadRound,
} from "./chessOlympiadPairing";

const player = (entry, fallbackId) => ({
  id: entry?.player_id || fallbackId,
  elo: entry ? Number(entry.rating_used) : undefined,
  drawFactor: Number(entry?.individual_draw_factor ?? 1),
});
const result = (probabilities, random) => {
  const value = random();
  if (value < probabilities.teamAWin) return 1;
  if (value < probabilities.teamAWin + probabilities.draw) return 0.5;
  return 0;
};

export function simulateOlympiadTournament({
  teams,
  rounds = 11,
  effectiveMinutes,
  parameters,
  modelStrategy = CHESS_MODEL_STRATEGY,
  strengthReference,
  random = Math.random,
  baseline = [],
  officialPairings = [],
  shouldCancel = () => false,
  captureRounds = false,
}) {
  if (shouldCancel()) return null;
  const ranked = initialOlympiadRanking(teams);
  const state = new Map(
    ranked.map((team) => [
      team.id,
      {
        ...team,
        matchPoints: 0,
        boardPoints: 0,
        opponents: [],
        colours: [],
        hadBye: false,
      },
    ])
  );
  baseline.forEach((match) => {
    if (shouldCancel()) return;
    if (!match.completed) return;
    const a = state.get(match.team_a_id);
    const b = state.get(match.team_b_id);
    if (!a || !b) return;
    const aScore = Number(match.team_a_score);
    const bScore = Number(match.team_b_score);
    a.boardPoints += aScore;
    b.boardPoints += bScore;
    a.matchPoints += aScore > bScore ? 2 : aScore === bScore ? 1 : 0;
    b.matchPoints += bScore > aScore ? 2 : aScore === bScore ? 1 : 0;
    a.opponents.push(b.id);
    b.opponents.push(a.id);
  });
  const nextAfterCompleted =
    Math.max(
      0,
      ...baseline
        .filter((match) => match.completed)
        .map((match) => Number(match.round))
    ) + 1;
  const pendingRounds = officialPairings.filter(match => !match.completed).map(match => Number(match.round));
  const firstRound = pendingRounds.length ? Math.min(nextAfterCompleted, ...pendingRounds) : nextAfterCompleted;
  const roundTrace = [];
  for (let round = firstRound; round <= rounds; round += 1) {
    if (shouldCancel()) return null;
    const official = officialPairings.filter(
      (match) => Number(match.round) === round && !match.completed
    );
    const pairing = official.length
      ? {
          source: "official",
          matches: official
            .map((match) => ({
              id: match.id,
              storedBoards: match.boards || [],
              table: Number(match.match_number),
              teamA: state.get(match.team_a_id),
              teamB: state.get(match.team_b_id),
              teamAColour: match.team_a_colour || "white",
              teamBColour:
                (match.team_a_colour || "white") === "white"
                  ? "black"
                  : "white",
            }))
            .filter((match) => match.teamA && match.teamB),
          bye: null,
        }
      : { ...pairOlympiadRound([...state.values()], round), source: "simulated" };
    const tracedMatches = [];
    if (pairing.bye) {
      const bye = state.get(pairing.bye.id);
      bye.matchPoints += 1;
      bye.boardPoints += 2;
      bye.hadBye = true;
    }
    pairing.matches.forEach((match) => {
      if (shouldCancel()) return;
      const a = state.get(match.teamA.id);
      const b = state.get(match.teamB.id);
      const chooseRoster = (team) => Array.from({length: 4}, (_,index) => {
        const board = match.storedBoards?.[index];
        return team.roster.find(player => board && [board.white_player_id, board.black_player_id].includes(player.player_id)) || team.roster[index];
      });
      const rosterA = chooseRoster(a);
      const rosterB = chooseRoster(b);
      let scoreA = 0, scoreB = 0;
      const boardResults = [];
      for (let board = 0; board < 4; board += 1) {
        if (shouldCancel()) return;
        const aWhite =
          board % 2 === 0
            ? match.teamAColour === "white"
            : match.teamAColour !== "white";
        const probabilities = buildBoardProbabilities({
            teamAPlayer: player(rosterA[board], `${a.id}-${board}`),
            teamBPlayer: player(rosterB[board], `${b.id}-${board}`),
            teamAColor: aWhite ? "white" : "black",
            effectiveMinutes,
            parameters,
            modelStrategy,
            strengthReference,
          });
        const knownScores = boardScoresFromResult(match.storedBoards?.[board]?.result, aWhite ? "white" : "black");
        const boardScore = knownScores?.[0] ?? result(probabilities, random);
        scoreA += boardScore;
        scoreB += knownScores?.[1] ?? 1 - boardScore;
        if (captureRounds) boardResults.push({
          board: board + 1,
          teamAPlayer: rosterA[board],
          teamBPlayer: rosterB[board],
          teamAColor: aWhite ? "white" : "black",
          scoreA: boardScore,
          scoreB: knownScores?.[1] ?? 1 - boardScore,
          knownScores,
          officialResult: match.storedBoards?.[board]?.result || null,
          probabilities,
        });
      }
      a.boardPoints += scoreA;
      b.boardPoints += scoreB;
      a.matchPoints += scoreA > scoreB ? 2 : scoreA === scoreB ? 1 : 0;
      b.matchPoints += scoreB > scoreA ? 2 : scoreB === scoreA ? 1 : 0;
      a.opponents.push(b.id);
      b.opponents.push(a.id);
      a.colours.push(match.teamAColour);
      b.colours.push(match.teamBColour);
      if (captureRounds) tracedMatches.push({
        id: match.id,
        table: match.table,
        teamA: match.teamA,
        teamB: match.teamB,
        teamAColour: match.teamAColour,
        scoreA,
        scoreB,
        boardResults,
        probabilities: calculateTeamMatchProbabilities(
          boardResults.map(({ probabilities }) => probabilities),
          boardResults.map(({ knownScores }) => knownScores)
        ),
      });
    });
    if (captureRounds) roundTrace.push({
      round,
      source: pairing.source,
      matches: tracedMatches,
      table: olympiadStandings([...state.values()]).map((team) => ({ ...team })),
    });
    if (shouldCancel()) return null;
  }
  const table = olympiadStandings([...state.values()]);
  return captureRounds ? { table, rounds: roundTrace } : table;
}

export function buildOlympiadSimulationNextRound({ teams, baseline = [] }) {
  const ranked = initialOlympiadRanking(teams);
  const state = new Map(
    ranked.map((team) => [
      team.id,
      {
        ...team,
        matchPoints: 0,
        boardPoints: 0,
        opponents: [],
        colours: [],
        hadBye: false,
      },
    ])
  );
  baseline
    .filter((match) => match.completed)
    .forEach((match) => {
      const a = state.get(match.team_a_id);
      const b = state.get(match.team_b_id);
      if (!a || !b) return;
      const aScore = Number(match.team_a_score);
      const bScore = Number(match.team_b_score);
      a.boardPoints += aScore;
      b.boardPoints += bScore;
      a.matchPoints += aScore > bScore ? 2 : aScore === bScore ? 1 : 0;
      b.matchPoints += bScore > aScore ? 2 : aScore === bScore ? 1 : 0;
      a.opponents.push(b.id);
      b.opponents.push(a.id);
      if (match.team_a_colour) {
        a.colours.push(match.team_a_colour);
        b.colours.push(match.team_a_colour === "white" ? "black" : "white");
      }
    });
  const round =
    Math.max(
      0,
      ...baseline
        .filter((match) => match.completed)
        .map((match) => Number(match.round))
    ) + 1;
  return pairOlympiadRound([...state.values()], round);
}
